// ./src/components/features/Connectors/ConfigEditors/AwsEventBridgeConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Select,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AwsEventBridgeConnectorConfig } from "@/types";

export interface AwsEventBridgeConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AwsEventBridgeConnectorConfig = {
  accessKeyId: "",
  secretAccessKey: "",
  region: "",
  eventBusName: "default",
  source: "formsubmits",
  detailType: "FormSubmission",
};

// Common AWS regions
const AWS_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-1", label: "US West (N. California)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-west-3", label: "Europe (Paris)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];

/**
 * Parse and validate AWS EventBridge connector configuration
 */
const parseAwsEventBridgeConfig = (
  jsonString: string
): AwsEventBridgeConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const AwsEventBridgeConnectorConfigEditor: React.FC<
  AwsEventBridgeConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AwsEventBridgeConnectorConfig>(() =>
    parseAwsEventBridgeConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAwsEventBridgeConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AwsEventBridgeConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessKeyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessKeyId: e.target.value });
  };

  const handleSecretAccessKeyChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, secretAccessKey: e.target.value });
  };

  const handleRegionChange = (value: string | null) => {
    updateConfig({ ...config, region: value || "" });
  };

  const handleEventBusNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, eventBusName: e.target.value });
  };

  const handleSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, source: e.target.value });
  };

  const handleDetailTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, detailType: e.target.value });
  };

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Create access keys in{" "}
          <Anchor
            href="https://console.aws.amazon.com/iam/"
            target="_blank"
            rel="noopener noreferrer"
          >
            AWS IAM Console
          </Anchor>
          {". Manage event buses in "}
          <Anchor
            href="https://console.aws.amazon.com/events/"
            target="_blank"
            rel="noopener noreferrer"
          >
            EventBridge Console
          </Anchor>
        </Text>
      </Alert>

      {/* Access Key ID */}
      <TextInput
        label="Access Key ID"
        placeholder="AKIAIOSFODNN7EXAMPLE"
        description="AWS Access Key ID"
        value={config.accessKeyId}
        onChange={handleAccessKeyIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Secret Access Key */}
      <TextInput
        label="Secret Access Key"
        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
        description="AWS Secret Access Key"
        value={config.secretAccessKey}
        onChange={handleSecretAccessKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Region */}
      <Select
        label="Region"
        placeholder="Select AWS region"
        description="AWS region for EventBridge"
        data={AWS_REGIONS}
        value={config.region}
        onChange={handleRegionChange}
        disabled={disabled}
        required
        size="md"
        searchable
      />

      {/* Event Bus Name */}
      <TextInput
        label="Event Bus Name"
        placeholder="default"
        description='Use "default" for the default event bus, or specify a custom bus name or ARN'
        value={config.eventBusName || "default"}
        onChange={handleEventBusNameChange}
        disabled={disabled}
        size="md"
      />

      {/* Source */}
      <TextInput
        label="Source"
        placeholder="formsubmits"
        description="Event source identifier (typically your application name)"
        value={config.source || "formsubmits"}
        onChange={handleSourceChange}
        disabled={disabled}
        size="md"
      />

      {/* Detail Type */}
      <TextInput
        label="Detail Type"
        placeholder="FormSubmission"
        description="Event type name used for filtering in rules"
        value={config.detailType || "FormSubmission"}
        onChange={handleDetailTypeChange}
        disabled={disabled}
        size="md"
      />

      {/* Event Structure Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Event Structure:
        </Text>
        <Text size="xs" component="div">
          Events are published with the following structure:
          <br />
          <br />
          <Code block>
            {`{
  "source": "${config.source || "formsubmits"}",
  "detail-type": "${config.detailType || "FormSubmission"}",
  "detail": { /* submission data */ },
  "resources": [
    "form/{formId}",
    "submission/{requestId}"
  ]
}`}
          </Code>
        </Text>
      </Alert>

      {/* Rules Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="yellow" variant="light">
        <Text size="sm">
          <strong>Note:</strong> Create EventBridge rules to route events to
          targets like Lambda, SQS, or Step Functions. Filter by{" "}
          <Code>source</Code> and <Code>detail-type</Code> in your rule
          patterns.
        </Text>
      </Alert>
    </Stack>
  );
};
